import { MutationHookOptions, gql, useMutation } from '@apollo/client';
import {
  UpsertSubmissionUserMutation,
  UpsertSubmissionUserMutationVariables,
} from '../gql/graphql';

import { GET_SUBMISSION } from './submission.query';
import { GET_SUBMISSIONS } from './submissions.query';
import { convergeLogger } from '@converge/config';
import { graphql } from '../gql';

export const UPSERT_SUBMISSION_USER = graphql(`
  mutation upsertSubmissionUser(
    $submissionUuid: String!
    $userUuid: String
    $userGroup: UserGroup!
  ) {
    upsertSubmissionUser(
      submissionUuid: $submissionUuid
      userUuid: $userUuid
      userGroup: $userGroup
    ) {
      uuid
      status
      subStatus
      users {
        edges {
          cursor
          node {
            uuid
            username
            userId
          }
        }
      }
    }
  }
`);

export function useUpsertSubmissionUserMutation(
  options: MutationHookOptions<
    UpsertSubmissionUserMutation,
    UpsertSubmissionUserMutationVariables
  > = {}
) {
  const [upsertSubmissionUser, { loading, error, reset, client }] = useMutation(
    UPSERT_SUBMISSION_USER,
    {
      // TODO: Remove refetch queries when we switch to a subscription-based approach,
      // because of a race condition with polling GET_SUBMISSIONS
      awaitRefetchQueries: true,
      refetchQueries: [GET_SUBMISSION, GET_SUBMISSIONS],
      update: (cache, { data: updatedData }) => {
        if (!updatedData?.upsertSubmissionUser) {
          convergeLogger.warn(
            `No data returned from upsertSubmissionUser mutation. getSubmissions query will be refetched.`
          );
          client.refetchQueries({
            include: [GET_SUBMISSIONS],
          });
          return;
        }

        const id = cache.identify(updatedData.upsertSubmissionUser);
        const fragment = gql`
          fragment SubmisionDetails on Submission {
            uuid
            status
            subStatus
            users {
              edges {
                cursor
                node {
                  uuid
                  username
                  userId
                }
              }
            }
          }
        `;

        const fragmentData = cache.readFragment({
          id,
          fragment,
        });

        if (!fragmentData) {
          convergeLogger.warn(
            `Cached data for ${id} was not found. getSubmissions query will be refetched.`
          );
          client.refetchQueries({
            include: [GET_SUBMISSIONS, GET_SUBMISSION],
          });
          return;
        }

        convergeLogger.log(
          `Updating cached data for ${id} with new user list...`,
          updatedData.upsertSubmissionUser.users
        );

        cache.updateFragment(
          {
            id,
            fragment,
          },
          (data) => {
            const newData: typeof updatedData.upsertSubmissionUser = {
              ...data,
              status: updatedData.upsertSubmissionUser.status,
              subStatus: updatedData.upsertSubmissionUser.subStatus,
              users: updatedData.upsertSubmissionUser.users,
            };

            convergeLogger.info('Cache updated', data);

            return newData;
          }
        );
      },
      ...options,
    }
  );

  return {
    upsertSubmissionUser,
    loading,
    error,
    reset,
    client,
  };
}
